'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { signIn } from 'next-auth/react';
import { LoginFormData } from '@/lib/validations/auth';
import AuthForm from '@/components/AuthForm';
import { useAuth } from '@/context/AuthContext';
import { Skeleton } from '@/components/ui/skeleton'; // Import Skeleton

export function LoginHandler() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectPath = searchParams?.get('redirect') || '/dashboard';
  const callbackError = searchParams?.get('error');
  const from = searchParams?.get('from'); // Add a 'from' parameter to detect logout
  
  const { isAuthenticated, isLoading: isAuthLoading, isAdmin, user } = useAuth(); // Added user from context
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Handle errors from NextAuth
  useEffect(() => {
    if (callbackError) {
      console.log(`Login Error from URL: ${callbackError}`);
      
      // Skip showing error if error is 'unauthorized' and user came from logout
      if (callbackError === 'unauthorized' && from === 'logout') {
        // Just clear the URL parameters without setting an error message
        router.replace('/login', { scroll: false });
        return;
      }
      
      switch (callbackError) {
        case 'CredentialsSignin':
          setError('Invalid email or password. Please try again.');
          break;
        case 'unauthorized':
          // Don't show error for simple unauthorized - it's often just a session expiration
          break;
        default:
          setError(`An error occurred during login (${callbackError}). Please try again.`);
      }
      // Clear the error from the URL to prevent it showing again on refresh
      router.replace('/login', { scroll: false }); 
    }
  }, [callbackError, from, router]);

  // Redirect if already authenticated
  useEffect(() => {
    // Wait until auth status is determined
    if (!isAuthLoading && isAuthenticated) {
      console.log("Auth status determined - User is authenticated");
      console.log("User object:", user);
      
      // If the session indicates email is not verified, redirect to verification page
      if (user && user.emailVerified === false) {
        console.log(`User email not verified, redirecting to verification page: ${user.email}`);
        router.push(`/verify?needsVerification=true&email=${encodeURIComponent(user.email)}`);
        return;
      }
      
      // Otherwise, redirect to the appropriate dashboard
      const targetPath = isAdmin ? '/admin' : '/dashboard';
      console.log(`User authenticated (${isAdmin ? 'Admin' : 'User'}), redirecting to ${targetPath}`);
      router.push(targetPath);
    } else if (!isAuthLoading) {
      console.log("Auth status determined - User is NOT authenticated");
    }
  }, [isAuthLoading, isAuthenticated, isAdmin, user, router]);

  const handleSubmit = async (data: LoginFormData) => {
    console.log('Login submit handler called with data:', {
      email: data.email,
      passwordLength: data.password ? data.password.length : 0
    });
    
    setIsSubmitting(true);
    setError(undefined);

    try {
      // Normalize email to lowercase before sending
      const normalizedEmail = data.email.toLowerCase().trim();
      
      console.log('Attempting to sign in with NextAuth - Credentials Provider');
      const res = await signIn('credentials', {
        email: normalizedEmail,
        password: data.password,
        redirect: false, // Important: handle redirect manually based on response
        callbackUrl: redirectPath, // Pass intended redirect path
      });

      console.log('Sign in response:', res);

      if (!res) {
        console.error('No response received from authentication provider');
        throw new Error('No response from authentication provider');
      }

      if (res.error) {
        console.error('NextAuth Signin Error:', res.error);
        switch (res.error) {
          case 'CredentialsSignin':
            setError('Invalid email or password. Please check your credentials.');
            break;
          case 'unauthorized':
            // Don't show error for simple unauthorized - it's often just a session expiration
            break;
          default:
            setError(`Login failed (${res.error}). Please try again.`);
            break;
        }
      } else if (res.ok) {
        // Success - Let the useEffect handle the appropriate redirect
        console.log('Login successful via NextAuth');
      } else {
        // Handle unexpected case
        console.error('Unexpected response state:', res);
        setError('An unexpected issue occurred during login.');
      }
    } catch (error: unknown) {
      console.error('Login submission catch block error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(`An unexpected error occurred: ${errorMessage}. Please try again later.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render null or a loading indicator while checking auth state to prevent flicker
  if (isAuthLoading) {
    return <LoginLoadingSkeleton />; 
  }
  
  // If already authenticated, redirect is handled by useEffect, render nothing here to avoid flash
  if (isAuthenticated) {
      return null; 
  }

  // If not loading and not authenticated, show the login form
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
      <div className="w-full max-w-md">
        <AuthForm
          type="login"
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
          error={error}
        />
      </div>
    </div>
  );
}

// Simple loading skeleton for the Suspense fallback
export function LoginLoadingSkeleton() {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
      <div className="w-full max-w-md bg-white p-8 rounded-lg shadow-md space-y-6">
        <Skeleton className="h-8 w-1/2 mx-auto" /> {/* Title Skeleton */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-1/4" /> {/* Label Skeleton */}
          <Skeleton className="h-10 w-full" /> {/* Input Skeleton */}
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-1/4" /> {/* Label Skeleton */}
          <Skeleton className="h-10 w-full" /> {/* Input Skeleton */}
        </div>
        <Skeleton className="h-10 w-full mt-6" /> {/* Button Skeleton */}
        <div className="flex justify-between">
           <Skeleton className="h-4 w-1/3" /> {/* Link Skeleton */}
           <Skeleton className="h-4 w-1/3" /> {/* Link Skeleton */}
        </div>
      </div>
    </div>
  );
}